// Realtime sync via Supabase Realtime (broadcast channels).
// One channel per lesson, members can be teacher | student.
//
// Exposed surface is identical to the previous Firebase-based impl:
//   ensureLessonState(lessonId)
//   subscribeLessonState(lessonId, callback)
//   publishLessonState(lessonId, partial)
//   useLessonRealtime(lessonId, role) → { remote, publishTab, publishTool,
//                                          publishCursor, publishWhiteboardPatch,
//                                          publishController }
//
// Follow Mode: `controller` field — 'teacher' (student is read-only) or
// 'free' (both can interact). publishController toggles it; remote.controller
// is the current authoritative value.

import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

const defaultState = {
  currentTab: 0,
  tool: 'select',
  teacherCursor: null,
  wbPatchSeq: 0,
  wbTargetId: null,
  wbPatch: null,
  studentWbPatchSeq: 0,
  studentWbTargetId: null,
  studentWbPatch: null,
  controller: 'free',  // 'free' | 'teacher' (Follow Mode)
  wbViewport: null,    // { x, y, scale } — teacher viewport for Follow Mode
  sessionStartedAt: null, // ISO timestamp when teacher pressed Start Lesson
  scrollRatio: 0, // 0..1, fraction of window scrolled — independent of window height
  editorContents: null, // map of editor id → content snapshot (live text sync)
  editorContentsSeq: 0,
  studentEditorContents: null, // student's live typing — mirrored to teacher
  studentEditorContentsSeq: 0,
  studentCursor: null, // { x, y, name } — student cursor position for teacher to see
  mediaState: null,   // { src, action: 'play'|'pause', currentTime }
  mediaStateSeq: 0,
  studentMediaState: null, // same shape, student's own play/pause — mirrored to teacher
  studentMediaStateSeq: 0,
};

// Per-lesson state + channel cache. Multiple consumers in the same tab share one channel.
const channels = new Map();          // lessonId → RealtimeChannel
const stateByLesson = new Map();     // lessonId → latest state snapshot
const listeners = new Map();         // lessonId → Set<callback>
const presenceByLesson = new Map();  // lessonId → array of present participants
const presenceListeners = new Map(); // lessonId → Set<callback>
const reactionListeners = new Map();  // lessonId → Set<callback>
const exerciseListeners = new Map(); // lessonId → Set<callback>
// Local role of whoever's driving THIS browser tab's connection to a given
// lesson channel ('teacher' | 'student'). Used by the presence-join resync
// below — a channel is shared module-level state, so it doesn't otherwise
// know which side of the call it's running on.
const roleByLesson = new Map();

function getOrCreateChannel(lessonId, presenceKey) {
  let channel = channels.get(lessonId);
  if (channel) return channel;

  channel = supabase.channel(`lesson-${lessonId}`, {
    config: {
      broadcast: { self: false, ack: false },
      presence: { key: presenceKey || `anon-${Math.random().toString(36).slice(2, 8)}` },
    },
  });

  channel.on('broadcast', { event: 'state' }, ({ payload }) => {
    const next = { ...(stateByLesson.get(lessonId) || defaultState), ...payload };
    stateByLesson.set(lessonId, next);
    const subs = listeners.get(lessonId);
    if (subs) subs.forEach((cb) => cb(next));
  });

  channel.on('broadcast', { event: 'reaction' }, ({ payload }) => {
    const subs = reactionListeners.get(lessonId);
    if (subs) subs.forEach((cb) => cb(payload));
  });

  channel.on('broadcast', { event: 'exercise' }, ({ payload }) => {
    const subs = exerciseListeners.get(lessonId);
    if (subs) subs.forEach((cb) => cb(payload));
  });

  // Presence sync — emits a flat list of currently-tracked participants
  channel.on('presence', { event: 'sync' }, () => {
    const raw = channel.presenceState() || {};
    const flat = [];
    Object.values(raw).forEach((entries) => {
      entries.forEach((e) => flat.push(e));
    });
    presenceByLesson.set(lessonId, flat);
    const subs = presenceListeners.get(lessonId);
    if (subs) subs.forEach((cb) => cb(flat));
  });

  // Resync on join: a peer who joins mid-lesson (or refreshes) starts from
  // `defaultState` locally — they've never seen any of the teacher's
  // broadcasts up to that point, and `publishLessonState` only ever sends
  // the CHANGED fields, not a full snapshot. Without this, "what the
  // student sees" only catches up to "what the teacher sees" once the
  // teacher's NEXT action fires — until then the two views can differ on
  // tab, whiteboard content, controller lock, viewport, everything. When
  // the teacher's tab observes a genuinely new (non-self) participant
  // joining, it re-broadcasts its full current state so the joiner is
  // caught up immediately instead of waiting for the next incidental change.
  channel.on('presence', { event: 'join' }, ({ newPresences }) => {
    const myRole = roleByLesson.get(lessonId);
    if (myRole !== 'teacher') return;
    const otherJoined = (newPresences || []).some((p) => p.role && p.role !== myRole);
    if (!otherJoined) return;
    const cur = stateByLesson.get(lessonId);
    if (!cur) return;
    // Small delay so the joiner's own subscribe handshake (and their
    // `subscribeLessonState` synchronous initial callback) has settled
    // before this arrives — avoids a race where the resync lands before
    // their listener is registered.
    setTimeout(() => {
      channel.send({ type: 'broadcast', event: 'state', payload: cur }).catch(() => {});
    }, 300);
  });

  channel.subscribe(async (status) => {
    if (status === 'SUBSCRIBED') {
      // Trigger an initial sync — the consumer can call trackPresence afterwards
      try { await channel.track({ joinedAt: new Date().toISOString() }); } catch (_) {}
    }
  });

  channels.set(lessonId, channel);
  return channel;
}

export function ensureLessonState(lessonId) {
  if (!stateByLesson.has(lessonId)) stateByLesson.set(lessonId, { ...defaultState });
  getOrCreateChannel(lessonId);
}

export function subscribeLessonState(lessonId, callback) {
  ensureLessonState(lessonId);
  // Emit current snapshot synchronously
  callback(stateByLesson.get(lessonId));
  let subs = listeners.get(lessonId);
  if (!subs) {
    subs = new Set();
    listeners.set(lessonId, subs);
  }
  subs.add(callback);
  return () => {
    subs.delete(callback);
    if (subs.size === 0) {
      listeners.delete(lessonId);
      const ch = channels.get(lessonId);
      if (ch) {
        supabase.removeChannel(ch);
        channels.delete(lessonId);
      }
      stateByLesson.delete(lessonId);
    }
  };
}

export async function publishLessonState(lessonId, partial) {
  const channel = getOrCreateChannel(lessonId);
  const next = { ...(stateByLesson.get(lessonId) || defaultState), ...partial };
  stateByLesson.set(lessonId, next);
  // Local listeners hear the change immediately
  const subs = listeners.get(lessonId);
  if (subs) subs.forEach((cb) => cb(next));
  try {
    await channel.send({ type: 'broadcast', event: 'state', payload: partial });
  } catch (err) {
    // Network/permission errors are non-fatal — state has already been
    // updated locally so the UI stays responsive. Still log it: this used
    // to be swallowed completely, which made a broadcast that fails
    // because the payload is too large (e.g. a drawing's base64 image
    // data inside editorContents) indistinguishable from one that never
    // happened at all — the peer just silently never got the update.
    console.warn('[realtime] publishLessonState broadcast failed:', err?.message || err);
  }
}

export function useLessonRealtime(lessonId, role /* 'teacher' | 'student' */, userInfo) {
  const [remote, setRemote] = useState(defaultState);
  const [participants, setParticipants] = useState([]);
  const throttles = useRef({});
  // Pending trailing-edge timers, keyed the same as `throttles` — see the
  // `throttle` helper below for why these exist.
  const throttleTimers = useRef({});

  useEffect(() => {
    if (!lessonId) return;
    // Set BEFORE getOrCreateChannel so the presence-join handler registered
    // inside it (if this is the first caller to create the channel) already
    // knows which side of the call this tab is on.
    roleByLesson.set(lessonId, role);
    ensureLessonState(lessonId);
    const unsub = subscribeLessonState(lessonId, setRemote);

    // Track this user in presence so the other side knows we're here
    const channel = getOrCreateChannel(lessonId, userInfo?.id || role);
    let presSubs = presenceListeners.get(lessonId);
    if (!presSubs) {
      presSubs = new Set();
      presenceListeners.set(lessonId, presSubs);
    }
    presSubs.add(setParticipants);

    // Track our metadata after the channel is subscribed
    const trackInterval = setInterval(() => {
      if (channel.state === 'joined') {
        channel.track({
          role,
          id: userInfo?.id || null,
          name: userInfo?.name || (role === 'teacher' ? 'Teacher' : 'Student'),
          joinedAt: new Date().toISOString(),
        }).catch(() => {});
        clearInterval(trackInterval);
      }
    }, 200);

    return () => {
      clearInterval(trackInterval);
      presSubs.delete(setParticipants);
      try { channel.untrack(); } catch (_) {}
      unsub && unsub();
      Object.values(throttleTimers.current).forEach(t => t && clearTimeout(t));
      throttleTimers.current = {};
    };
  }, [lessonId, role, userInfo?.id, userInfo?.name]);

  // Leading-edge throttle that ALSO guarantees a trailing call — a call
  // that lands inside the window used to be silently dropped outright
  // (no timer, nothing rescheduled it), so if the caller's very last
  // update in a burst happened to land within `wait` of the previous one,
  // it was never sent at all: the peer got stuck on a stale snapshot with
  // no error anywhere (this is exactly what made teacher→student image
  // drawing and image text updates intermittently vanish — the periodic
  // 450ms drawing flush and every keystroke of text both go through this
  // same throttle, and the final flush/keystroke right before the user
  // stopped was often the one that got dropped). Now a call inside the
  // window schedules a trailing call with its own (latest) args instead,
  // so the last update always eventually gets through.
  const throttle = (key, fn, wait = 50) => {
    const now = Date.now();
    const prev = throttles.current[key] || 0;
    const remaining = wait - (now - prev);
    if (remaining <= 0) {
      if (throttleTimers.current[key]) {
        clearTimeout(throttleTimers.current[key]);
        throttleTimers.current[key] = null;
      }
      throttles.current[key] = now;
      fn();
      return;
    }
    if (throttleTimers.current[key]) clearTimeout(throttleTimers.current[key]);
    throttleTimers.current[key] = setTimeout(() => {
      throttleTimers.current[key] = null;
      throttles.current[key] = Date.now();
      fn();
    }, remaining);
  };

  const publishTab = useCallback((tabIndex) => {
    if (role !== 'teacher') return;
    publishLessonState(lessonId, { currentTab: tabIndex });
  }, [lessonId, role]);

  const publishTool = useCallback((tool) => {
    if (role !== 'teacher') return;
    publishLessonState(lessonId, { tool });
  }, [lessonId, role]);

  const publishCursor = useCallback((cursor) => {
    if (role !== 'teacher') return;
    throttle('cursor', () => publishLessonState(lessonId, { teacherCursor: cursor }), 40);
  }, [lessonId, role]);

  const publishWhiteboardPatch = useCallback((targetId, patch) => {
    if (role !== 'teacher') return;
    throttle(
      'wb',
      () => {
        const cur = stateByLesson.get(lessonId) || defaultState;
        publishLessonState(lessonId, {
          wbPatchSeq: (cur.wbPatchSeq || 0) + 1,
          wbTargetId: targetId,
          wbPatch: patch,
        });
      },
      70
    );
  }, [lessonId, role]);

  // Follow Mode: teacher can flip control between 'free' and 'teacher' (locked).
  const publishController = useCallback((value /* 'free' | 'teacher' */) => {
    if (role !== 'teacher') return;
    publishLessonState(lessonId, { controller: value });
  }, [lessonId, role]);

  // Variant D — formal "Start Lesson" / "End Lesson". Records start timestamp
  // in the shared state so both sides know a session is active.
  const publishSession = useCallback((action /* 'start' | 'end' */) => {
    if (role !== 'teacher') return;
    publishLessonState(lessonId, {
      sessionStartedAt: action === 'start' ? new Date().toISOString() : null,
    });
  }, [lessonId, role]);

  // Reactions — both roles can send
  const publishReaction = useCallback((emoji) => {
    broadcastReaction(lessonId, emoji);
  }, [lessonId]);

  // Student cursor — student broadcasts position to teacher
  const publishStudentCursor = useCallback((cursor) => {
    if (role !== 'student') return;
    throttle('studentCursor', () => publishLessonState(lessonId, { studentCursor: cursor }), 40);
  }, [lessonId, role]);

  // Student whiteboard patch — student broadcasts shape changes to teacher
  const publishStudentWhiteboardPatch = useCallback((targetId, patch) => {
    if (role !== 'student') return;
    throttle(
      'studentWb',
      () => {
        const cur = stateByLesson.get(lessonId) || defaultState;
        publishLessonState(lessonId, {
          studentWbPatchSeq: (cur.studentWbPatchSeq || 0) + 1,
          studentWbTargetId: targetId,
          studentWbPatch: patch,
        });
      },
      70
    );
  }, [lessonId, role]);

  // Viewport sync — teacher broadcasts pan+zoom so student follows in Follow Mode
  const publishViewport = useCallback((viewport) => {
    if (role !== 'teacher') return;
    throttle('viewport', () => publishLessonState(lessonId, { wbViewport: viewport }), 80);
  }, [lessonId, role]);

  // Scroll sync — publish as a 0..1 ratio so it survives different viewport heights
  const publishScroll = useCallback((scrollRatio) => {
    if (role !== 'teacher') return;
    throttle('scroll', () => publishLessonState(lessonId, { scrollRatio }), 60);
  }, [lessonId, role]);

  // Editor content sync — fast snapshot of editorContents map (feels live)
  const publishEditorContents = useCallback((contents) => {
    if (role !== 'teacher') return;
    throttle('editor', () => {
      const cur = stateByLesson.get(lessonId) || defaultState;
      publishLessonState(lessonId, {
        editorContents: contents,
        editorContentsSeq: (cur.editorContentsSeq || 0) + 1,
      });
    }, 80);
  }, [lessonId, role]);

  // Student editor content sync — student broadcasts live typing to teacher
  const publishStudentEditorContents = useCallback((contents) => {
    if (role !== 'student') return;
    throttle('studentEditor', () => {
      const cur = stateByLesson.get(lessonId) || defaultState;
      publishLessonState(lessonId, {
        studentEditorContents: contents,
        studentEditorContentsSeq: (cur.studentEditorContentsSeq || 0) + 1,
      });
    }, 80);
  }, [lessonId, role]);

  // Media playback sync — teacher broadcasts play/pause of video/audio/YouTube in doc mode
  const publishMediaState = useCallback((src, action, currentTime) => {
    if (role !== 'teacher') return;
    const cur = stateByLesson.get(lessonId) || defaultState;
    publishLessonState(lessonId, {
      mediaState: { src, action, currentTime: currentTime || 0 },
      mediaStateSeq: (cur.mediaStateSeq || 0) + 1,
    });
  }, [lessonId, role]);

  // Student side of the same sync — lets the student's own play/pause be
  // heard/seen by the teacher too, same as the whiteboard already lets both
  // sides act simultaneously.
  const publishStudentMediaState = useCallback((src, action, currentTime) => {
    if (role !== 'student') return;
    const cur = stateByLesson.get(lessonId) || defaultState;
    publishLessonState(lessonId, {
      studentMediaState: { src, action, currentTime: currentTime || 0 },
      studentMediaStateSeq: (cur.studentMediaStateSeq || 0) + 1,
    });
  }, [lessonId, role]);

  return {
    remote,
    participants,
    publishTab,
    publishTool,
    publishCursor,
    publishWhiteboardPatch,
    publishStudentWhiteboardPatch,
    publishController,
    publishSession,
    publishViewport,
    publishScroll,
    publishEditorContents,
    publishStudentEditorContents,
    publishReaction,
    publishStudentCursor,
    publishMediaState,
    publishStudentMediaState,
  };
}

export function subscribeExerciseStates(lessonId, callback) {
  ensureLessonState(lessonId);
  let subs = exerciseListeners.get(lessonId);
  if (!subs) { subs = new Set(); exerciseListeners.set(lessonId, subs); }
  subs.add(callback);
  return () => subs.delete(callback);
}

export async function broadcastExerciseState(lessonId, exerciseId, role, state) {
  const channel = getOrCreateChannel(lessonId);
  try {
    await channel.send({ type: 'broadcast', event: 'exercise', payload: { exerciseId, role, state } });
  } catch (_) {}
}

export function subscribeReactions(lessonId, callback) {
  ensureLessonState(lessonId);
  let subs = reactionListeners.get(lessonId);
  if (!subs) { subs = new Set(); reactionListeners.set(lessonId, subs); }
  subs.add(callback);
  return () => subs.delete(callback);
}

export async function broadcastReaction(lessonId, emoji) {
  const channel = getOrCreateChannel(lessonId);
  try {
    await channel.send({ type: 'broadcast', event: 'reaction', payload: { emoji } });
  } catch (_) {}
}

// Back-compat shim — old code may import this; not needed anymore.
export function initFirebaseIfConfigured() {
  return { app: null, db: null };
}
